/**
 * PLAYER AVATARS — a player's profile picture is a BLOSSOM they design: petal shape and count, layers, a
 * handful of shape sliders, two colours and how they paint the flower, a pattern, a centre, a backdrop, and a
 * BORDER (frame) around the square box. This module is the data: the spec, the option lists the editor offers
 * (with player-facing labels), the coercion every reader goes through, presets, random flowers, and the
 * border catalogue. The drawing is `ui/avatar/AvatarArt.tsx`; the editor is `ui/avatar/AvatarEditorModal.tsx`.
 *
 * WHERE IT LIVES: in the SAVE (`SaveData.avatar`). The server already reads a player's save to build their
 * public profile (stats, badges), so the avatar reaches every profile view, the Gardeners directory and any
 * future leaderboard through that same path (server/profiles.js) — no table, no second write path. It is
 * edited in the game, where the save is.
 *
 * THE SERVER VALIDATES IT TOO, with its own copy of `coerceAvatar` (server/avatar.js — the server is plain JS
 * and imports nothing from src/). The two must agree; `avatar.test.ts` runs both over the same inputs.
 * Change a field, a range or an option list here and change it there in the same commit.
 *
 * THERE IS NO SIZE. Every flower is scaled to fill its box to the same radius (ui/avatar/AvatarArt.tsx
 * `FIT_RADIUS`), so avatars read at one size in any list — the shape sliders change its proportions, never how
 * big it sits in the frame. (A `size` from an older save is dropped by the coercion.)
 *
 * EVERY NUMBER IS 0..1 and every choice is a short id, so a spec is a few hundred bytes of JSON, a slider
 * maps straight onto a field, and an out-of-range or unknown value from an old or hand-edited save clamps to
 * something drawable instead of breaking the picture.
 */

import { cosmeticEarned, type CosmeticAccess } from "./cosmetics";

export const AVATAR_VERSION = 1;

export type PetalShape = "round" | "oval" | "pointed" | "heart" | "spoon" | "star" | "fringed" | "ruffled";
export type PaintMode = "solid" | "fade" | "glow" | "alternate" | "rings" | "halves" | "outline";
export type PatternKind = "none" | "spots" | "stripes" | "veins" | "speckle" | "tipped" | "eye" | "picotee" | "bands";
export type PatternInk = "a" | "b" | "light" | "dark";
export type CentreStyle = "disc" | "seeds" | "stamens" | "button" | "star" | "none";
export type Backdrop = "night" | "moss" | "dusk" | "dawn" | "pond" | "ember" | "mist" | "plain";

export interface AvatarSpec {
  v: number;
  /** ---- PETALS ---- */
  shape: PetalShape;
  /** petals per ring, 3..21 */
  count: number;
  /** rings of petals, 1..4 */
  layers: number;
  /** 0..1 — how far neighbouring petals overlap (their width against the gap between them) */
  overlap: number;
  /** 0..1 — round tip → needle tip */
  point: number;
  /** 0..1 — rings twist against each other and petals lean like a pinwheel */
  spiral: number;
  /** 0..1 — how far from the centre the petals start */
  distance: number;
  /** 0..1 — how much smaller each inner ring is (a flat daisy → a deep, cupped rose) */
  depth: number;
  /** ---- COLOUR ---- */
  colorA: string;
  colorB: string;
  paint: PaintMode;
  /** ---- PATTERN ---- */
  pattern: PatternKind;
  patternInk: PatternInk;
  /** 0..1 — how much of it (size, density or strength, by pattern) */
  patternAmount: number;
  /** ---- CENTRE ---- */
  centre: CentreStyle;
  centreColor: string;
  /** 0..1 */
  centreSize: number;
  /** ---- THE BOX ---- */
  backdrop: Backdrop;
  /** border id (AVATAR_FRAMES) */
  frame: string;
}

/** An option the editor lists: the id stored in the spec, and what the player reads. */
export interface Choice<T extends string> { id: T; label: string; hint?: string }

export const PETAL_SHAPES: Choice<PetalShape>[] = [
  { id: "round",   label: "Round",    hint: "Soft, broad petals" },
  { id: "oval",    label: "Oval",     hint: "Long and even" },
  { id: "pointed", label: "Pointed",  hint: "Lance-shaped, tapering to a tip" },
  { id: "heart",   label: "Notched",  hint: "A heart-shaped notch at the tip" },
  { id: "spoon",   label: "Spoon",    hint: "Narrow at the base, wide at the end" },
  { id: "star",    label: "Slender",  hint: "Thin rays, like an aster" },
  { id: "fringed", label: "Fringed",  hint: "A toothed, frilly tip" },
  { id: "ruffled", label: "Ruffled",  hint: "Wavy edges all the way around" },
];
export const PAINT_MODES: Choice<PaintMode>[] = [
  { id: "solid",     label: "Solid",              hint: "Color 1 only" },
  { id: "fade",      label: "Fade along petals",  hint: "Color 1 at the base, color 2 at the tips" },
  { id: "glow",      label: "Glow from center",   hint: "Color 1 in the middle, color 2 at the rim" },
  { id: "alternate", label: "Alternating petals", hint: "Every other petal in color 2" },
  { id: "rings",     label: "Alternating rings",  hint: "Each ring of petals switches color" },
  { id: "halves",    label: "Two-tone halves",    hint: "Each petal split down its middle" },
  { id: "outline",   label: "Outlined",           hint: "Color 1 petals with a color 2 rim" },
];
export const PATTERNS: Choice<PatternKind>[] = [
  { id: "none",    label: "None" },
  { id: "spots",   label: "Spots",    hint: "Dots near the base, like a foxglove" },
  { id: "speckle", label: "Speckles", hint: "Fine freckles all over" },
  { id: "stripes", label: "Stripes",  hint: "Lines running along each petal" },
  { id: "veins",   label: "Veins",    hint: "A midrib with branching veins" },
  { id: "bands",   label: "Bands",    hint: "Chevrons across the petals" },
  { id: "tipped",  label: "Dipped tips", hint: "Tips dipped in color" },
  { id: "eye",     label: "Eye ring", hint: "A ring of color around the center" },
  { id: "picotee", label: "Picotee edge", hint: "A thin colored rim" },
];
export const PATTERN_INKS: Choice<PatternInk>[] = [
  { id: "b",     label: "Color 2" },
  { id: "a",     label: "Color 1" },
  { id: "dark",  label: "Darker shade" },
  { id: "light", label: "Lighter shade" },
];
export const CENTRES: Choice<CentreStyle>[] = [
  { id: "disc",    label: "Disc" },
  { id: "seeds",   label: "Seed head" },
  { id: "stamens", label: "Stamens" },
  { id: "button",  label: "Button" },
  { id: "star",    label: "Star" },
  { id: "none",    label: "None" },
];
/** The backdrops — each a two-stop radial wash behind the flower, in the game's own palette. */
export const BACKDROPS: Array<Choice<Backdrop> & { inner: string; outer: string }> = [
  { id: "night", label: "Night garden", inner: "#1d3a35", outer: "#0b1715" },
  { id: "moss",  label: "Moss",         inner: "#36502d", outer: "#162214" },
  { id: "pond",  label: "Pond",         inner: "#1f4b5c", outer: "#0b1d26" },
  { id: "dusk",  label: "Dusk",         inner: "#4a2e57", outer: "#1a1024" },
  { id: "dawn",  label: "Dawn",         inner: "#e9c8a2", outer: "#9d6b5e" },
  { id: "ember", label: "Lantern",      inner: "#6b3d1f", outer: "#24130a" },
  { id: "mist",  label: "Mist",         inner: "#c9d6d0", outer: "#7f948d" },
  { id: "plain", label: "Plain",        inner: "#232a28", outer: "#232a28" },
];

/** The colour swatches the editor offers first (a native picker sits beside them for anything else). */
export const AVATAR_SWATCHES = [
  "#f2ece0", "#f7d6e0", "#ef8fb4", "#d9466f", "#9c1f3d", "#f4a259", "#f2c14e", "#fff1a8",
  "#b8e07a", "#5fae6e", "#2fd3b3", "#6ad7ff", "#4d7fd6", "#8f6bd8", "#c9a7ff", "#3b2a4a",
];

/** THE BORDERS a picture box can wear. `reward` ones are unlocked by an achievement or the shop through the
 *  same `frame:<id>` unlock key every other cosmetic uses (sim/cosmetics.ts). All four are paid by the skill
 *  ladder in sim/achievements.ts — Gardening and Bug Catching 25, Fishing 50 and the Triple Crown — and each
 *  hint names its rung. */
export interface AvatarFrame { id: string; name: string; reward?: boolean; hint?: string }
export const AVATAR_FRAMES: AvatarFrame[] = [
  { id: "simple",  name: "Simple" },
  { id: "twine",   name: "Garden Twine" },
  { id: "vine",    name: "Ivy Vine" },
  { id: "gilded",  name: "Gilded Filigree", reward: true, hint: "Gardening level 25" },
  { id: "laurel",  name: "Golden Laurel",   reward: true, hint: "Level 50 in all three skills" },
  { id: "frost",   name: "Hoarfrost",       reward: true, hint: "Fishing level 50" },
  { id: "aurora",  name: "Aurora",          reward: true, hint: "Bug Catching level 25" },
  { id: "cloisonne", name: "Founder's Cloisonné", reward: true, hint: "Kickstarter backer reward" },
];
export const DEFAULT_FRAME = "simple";
export function avatarFrame(id: string): AvatarFrame { return AVATAR_FRAMES.find((f) => f.id === id) ?? AVATAR_FRAMES[0]!; }
/** May this player put this border on? Free borders always; a reward border when its `frame:` unlock is held. */
export function frameOpen(id: string, access: CosmeticAccess): boolean {
  const f = AVATAR_FRAMES.find((x) => x.id === id);
  if (!f) return false;
  return !f.reward || cosmeticEarned("frame", id, access);
}

/** The flower a player starts from — a cool teal daisy on the night garden. */
export const DEFAULT_AVATAR: AvatarSpec = {
  v: AVATAR_VERSION,
  shape: "oval", count: 12, layers: 1, overlap: 0.45, point: 0.25, spiral: 0, distance: 0.12, depth: 0.4,
  colorA: "#f2ece0", colorB: "#6ad7ff", paint: "fade",
  pattern: "none", patternInk: "b", patternAmount: 0.5,
  centre: "seeds", centreColor: "#f2c14e", centreSize: 0.45,
  backdrop: "night", frame: DEFAULT_FRAME,
};

/** STARTING POINTS — a gallery of recognisable flowers to begin from, so nobody faces a page of sliders cold. */
export const AVATAR_PRESETS: Array<{ id: string; name: string; spec: AvatarSpec }> = [
  { id: "daisy", name: "Daisy", spec: { ...DEFAULT_AVATAR, colorA: "#f2ece0", colorB: "#f7d6e0", count: 16, overlap: 0.3, point: 0.1, paint: "solid", centre: "seeds", centreColor: "#f2c14e" } },
  { id: "rose", name: "Rose", spec: { ...DEFAULT_AVATAR, shape: "round", count: 6, layers: 4, overlap: 0.85, point: 0, spiral: 0.55, distance: 0, depth: 0.7, colorA: "#9c1f3d", colorB: "#ef8fb4", paint: "glow", centre: "none", centreSize: 0.2, backdrop: "dusk" } },
  { id: "lotus", name: "Lotus", spec: { ...DEFAULT_AVATAR, shape: "pointed", count: 8, layers: 3, overlap: 0.6, point: 0.75, spiral: 0.25, distance: 0.05, depth: 0.45, colorA: "#f2ece0", colorB: "#ef8fb4", paint: "fade", centre: "button", centreColor: "#f2c14e", backdrop: "pond" } },
  { id: "dahlia", name: "Dahlia", spec: { ...DEFAULT_AVATAR, shape: "spoon", count: 14, layers: 4, overlap: 0.55, point: 0.35, spiral: 0.4, distance: 0, depth: 0.55, colorA: "#f4a259", colorB: "#d9466f", paint: "rings", centre: "none", backdrop: "ember" } },
  { id: "aster", name: "Aster", spec: { ...DEFAULT_AVATAR, shape: "star", count: 21, layers: 2, overlap: 0.5, point: 0.6, spiral: 0.15, distance: 0.14, depth: 0.25, colorA: "#8f6bd8", colorB: "#c9a7ff", paint: "alternate", centre: "seeds", centreColor: "#f2c14e", backdrop: "night" } },
  { id: "poppy", name: "Poppy", spec: { ...DEFAULT_AVATAR, shape: "ruffled", count: 4, layers: 1, overlap: 0.9, point: 0, distance: 0, colorA: "#d9466f", colorB: "#3b2a4a", paint: "solid", pattern: "eye", patternInk: "b", patternAmount: 0.55, centre: "stamens", centreColor: "#3b2a4a", centreSize: 0.5, backdrop: "moss" } },
  { id: "pansy", name: "Pansy", spec: { ...DEFAULT_AVATAR, shape: "round", count: 5, layers: 1, overlap: 0.8, point: 0, distance: 0.02, colorA: "#4d7fd6", colorB: "#fff1a8", paint: "glow", pattern: "veins", patternInk: "dark", patternAmount: 0.6, centre: "disc", centreColor: "#f2c14e", centreSize: 0.3, backdrop: "mist" } },
  { id: "cosmos", name: "Cosmos", spec: { ...DEFAULT_AVATAR, shape: "heart", count: 8, layers: 1, overlap: 0.35, point: 0, distance: 0.08, colorA: "#ef8fb4", colorB: "#d9466f", paint: "fade", pattern: "stripes", patternInk: "b", patternAmount: 0.4, centre: "stamens", centreColor: "#f2c14e", backdrop: "dawn" } },
  { id: "foxglove", name: "Spotted Lily", spec: { ...DEFAULT_AVATAR, shape: "pointed", count: 6, layers: 1, overlap: 0.4, point: 0.6, spiral: 0.3, distance: 0.04, colorA: "#f7d6e0", colorB: "#9c1f3d", paint: "fade", pattern: "spots", patternInk: "b", patternAmount: 0.6, centre: "stamens", centreColor: "#9c1f3d", backdrop: "night" } },
];

// ---------------------------------------------------------------------------------------------------------
//  COERCION — every reader goes through here (the save, the server's echo, another player's profile)
// ---------------------------------------------------------------------------------------------------------

const HEX = /^#[0-9a-fA-F]{6}$/;
const clamp01 = (v: unknown, d: number) => (typeof v === "number" && Number.isFinite(v) ? Math.min(1, Math.max(0, v)) : d);
const intIn = (v: unknown, lo: number, hi: number, d: number) =>
  (typeof v === "number" && Number.isFinite(v) ? Math.min(hi, Math.max(lo, Math.round(v))) : d);
const oneOf = <T extends string>(v: unknown, list: Array<Choice<T>>, d: T): T =>
  (typeof v === "string" && list.some((c) => c.id === v) ? (v as T) : d);
const colour = (v: unknown, d: string) => (typeof v === "string" && HEX.test(v) ? v.toLowerCase() : d);

/** Untrusted JSON → a spec that always draws, or null when there is no avatar at all. */
export function coerceAvatar(raw: unknown): AvatarSpec | null {
  if (!raw || typeof raw !== "object" || Array.isArray(raw)) return null;
  const o = raw as Record<string, unknown>;
  const d = DEFAULT_AVATAR;
  return {
    v: AVATAR_VERSION,
    shape: oneOf(o.shape, PETAL_SHAPES, d.shape),
    count: intIn(o.count, 3, 21, d.count),
    layers: intIn(o.layers, 1, 4, d.layers),
    overlap: clamp01(o.overlap, d.overlap),
    point: clamp01(o.point, d.point),
    spiral: clamp01(o.spiral, d.spiral),
    distance: clamp01(o.distance, d.distance),
    depth: clamp01(o.depth, d.depth),
    colorA: colour(o.colorA, d.colorA),
    colorB: colour(o.colorB, d.colorB),
    paint: oneOf(o.paint, PAINT_MODES, d.paint),
    pattern: oneOf(o.pattern, PATTERNS, d.pattern),
    patternInk: oneOf(o.patternInk, PATTERN_INKS, d.patternInk),
    patternAmount: clamp01(o.patternAmount, d.patternAmount),
    centre: oneOf(o.centre, CENTRES, d.centre),
    centreColor: colour(o.centreColor, d.centreColor),
    centreSize: clamp01(o.centreSize, d.centreSize),
    backdrop: oneOf(o.backdrop, BACKDROPS, d.backdrop),
    frame: typeof o.frame === "string" && AVATAR_FRAMES.some((f) => f.id === o.frame) ? o.frame : d.frame,
  };
}

// ---------------------------------------------------------------------------------------------------------
//  RANDOM FLOWERS — the editor's "Surprise me", and the default picture for a player who never made one
// ---------------------------------------------------------------------------------------------------------

/** A small deterministic PRNG (mulberry32), so a seed always grows the same flower. */
function rng(seed: number): () => number {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}
/** A string → 32-bit seed (FNV-1a). */
export function seedOf(s: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < s.length; i++) { h ^= s.charCodeAt(i); h = Math.imul(h, 0x01000193); }
  return h >>> 0;
}

/**
 * A RANDOM FLOWER that still looks designed: the two colours come from the curated swatches (never two
 * near-identical ones), a pattern turns up a third of the time, and the frame is always the free default.
 */
export function randomAvatar(seed: number = (Math.random() * 2 ** 32) >>> 0): AvatarSpec {
  const r = rng(seed);
  const pick = <T,>(list: readonly T[]): T => list[Math.floor(r() * list.length)]!;
  const sw = AVATAR_SWATCHES;
  const ia = Math.floor(r() * sw.length);
  let ib = Math.floor(r() * sw.length);
  if (Math.abs(ib - ia) < 2) ib = (ia + 5 + Math.floor(r() * 6)) % sw.length;
  const layers = 1 + Math.floor(r() * r() * 4);
  return {
    v: AVATAR_VERSION,
    shape: pick(PETAL_SHAPES).id,
    count: 4 + Math.floor(r() * 15),
    layers,
    overlap: 0.25 + r() * 0.6,
    point: r(),
    spiral: r() < 0.5 ? 0 : r() * 0.7,
    distance: r() * 0.25,
    depth: 0.25 + r() * 0.5,
    colorA: sw[ia]!, colorB: sw[ib]!,
    paint: pick(PAINT_MODES).id,
    pattern: r() < 0.35 ? pick(PATTERNS.slice(1)).id : "none",
    patternInk: pick(PATTERN_INKS).id,
    patternAmount: 0.3 + r() * 0.5,
    centre: layers >= 3 && r() < 0.5 ? "none" : pick(CENTRES.slice(0, 5)).id,
    centreColor: pick(["#f2c14e", "#f4a259", "#3b2a4a", "#fff1a8", "#5fae6e"]),
    centreSize: 0.3 + r() * 0.4,
    backdrop: pick(BACKDROPS.slice(0, 7)).id,
    frame: DEFAULT_FRAME,
  };
}

/** The picture shown for a player who never designed one: a flower grown from their id, so a leaderboard of
 *  newcomers is a bed of different flowers rather than a column of identical placeholders. */
export function avatarFor(spec: AvatarSpec | null | undefined, playerId: string): AvatarSpec {
  return spec ?? randomAvatar(seedOf(playerId || "gardener"));
}
